import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  Box,
  Typography,
  FormControl,
  Switch,
  TextField,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const getInvalidEmailsInCsv = (csv) => {
  if (!csv || !csv.trim()) return [];
  return csv
    .split(',')
    .map((part) => part.trim())
    .filter((email) => email && !EMAIL_PATTERN.test(email));
};

const AdvancedEmailSettingsModal = ({
  open,
  onClose,
  agentId,
  customization,
  setCustomization,
  setAlert,
  onSaved,
}) => {
  const [screen, setScreen] = useState('main');
  const [saving, setSaving] = useState(false);
  const [deptDataLoading, setDeptDataLoading] = useState(false);
  const [tenantDepartments, setTenantDepartments] = useState([]);
  const [agentDepartmentEmails, setAgentDepartmentEmails] = useState([]);
  const [deptSelectValue, setDeptSelectValue] = useState('');
  const [deptFormMode, setDeptFormMode] = useState('add');
  const [deptFormId, setDeptFormId] = useState(null);
  const [deptFormName, setDeptFormName] = useState('');
  const [deptFormDescription, setDeptFormDescription] = useState('');
  const [deptFormReturnScreen, setDeptFormReturnScreen] = useState('main');
  const [deptSaveError, setDeptSaveError] = useState('');

  const [enabled, setEnabled] = useState(false);
  const [snapshotEnabled, setSnapshotEnabled] = useState(false);
  const [snapshotMappings, setSnapshotMappings] = useState([]);

  const fetchDepartmentEmailData = useCallback(async () => {
    if (!agentId) return;
    setDeptDataLoading(true);
    try {
      const [deptRes, mappingRes] = await Promise.all([
        axios.post('/departments/getAll'),
        axios.post('/agents/getAgentDepartmentEmails', null, { params: { agentId } }),
      ]);
      setTenantDepartments(deptRes.data || []);
      const mappings = (mappingRes.data || []).map((m) => ({
        departmentId: m.departmentId,
        departmentName: m.departmentName,
        departmentDescription: m.departmentDescription,
        emails: m.emails || '',
      }));
      setAgentDepartmentEmails(mappings);
      setSnapshotMappings(JSON.parse(JSON.stringify(mappings)));
    } catch (error) {
      console.error('Failed to load department email settings:', error);
    } finally {
      setDeptDataLoading(false);
    }
  }, [agentId]);

  useEffect(() => {
    if (open && agentId) {
      const initialEnabled = customization?.departmentEmailNotificationsEnabled === true;
      setEnabled(initialEnabled);
      setSnapshotEnabled(initialEnabled);
      setScreen('main');
      setDeptSaveError('');
      fetchDepartmentEmailData();
    }
  }, [open, agentId, fetchDepartmentEmailData]);

  const isDirty = () => {
    if (enabled !== snapshotEnabled) return true;
    return JSON.stringify(agentDepartmentEmails) !== JSON.stringify(snapshotMappings);
  };

  const handleRequestClose = () => {
    if (screen === 'deptForm') {
      setScreen(deptFormReturnScreen);
      setDeptSaveError('');
      return;
    }
    if (screen === 'departments') {
      setScreen('main');
      setDeptSaveError('');
      return;
    }
    if (isDirty()) {
      if (!window.confirm('You have unsaved changes. Discard them?')) {
        return;
      }
      setEnabled(snapshotEnabled);
      setAgentDepartmentEmails(JSON.parse(JSON.stringify(snapshotMappings)));
    }
    onClose();
  };

  const handleSelectDepartment = (e) => {
    const deptId = parseInt(e.target.value, 10);
    setDeptSelectValue('');
    if (!deptId) return;
    if (agentDepartmentEmails.some((m) => m.departmentId === deptId)) return;
    const dept = tenantDepartments.find((d) => d.id === deptId);
    if (!dept) return;
    setAgentDepartmentEmails((prev) => [
      ...prev,
      {
        departmentId: dept.id,
        departmentName: dept.name,
        departmentDescription: dept.description,
        emails: '',
      },
    ]);
  };

  const handleDepartmentEmailsChange = (departmentId) => (e) => {
    const value = e.target.value;
    setAgentDepartmentEmails((prev) =>
      prev.map((m) => (m.departmentId === departmentId ? { ...m, emails: value } : m))
    );
  };

  const handleRemoveDepartmentMapping = (departmentId) => {
    setAgentDepartmentEmails((prev) => prev.filter((m) => m.departmentId !== departmentId));
  };

  const syncDepartmentInMappings = (departmentId, name, description) => {
    setAgentDepartmentEmails((prev) =>
      prev.map((m) =>
        m.departmentId === departmentId
          ? { ...m, departmentName: name, departmentDescription: description }
          : m
      )
    );
  };

  const openEditDeptForm = (dept) => {
    setDeptSaveError('');
    setDeptFormMode('edit');
    setDeptFormId(dept.id);
    setDeptFormName(dept.name || '');
    setDeptFormDescription(dept.description || '');
    setDeptFormReturnScreen('departments');
    setScreen('deptForm');
  };

  const openAddDeptForm = (returnScreen = 'main') => {
    setDeptSaveError('');
    setDeptFormMode('add');
    setDeptFormId(null);
    setDeptFormName('');
    setDeptFormDescription('');
    setDeptFormReturnScreen(returnScreen);
    setScreen('deptForm');
  };

  const handleSaveDepartment = async () => {
    const name = deptFormName.trim();
    if (!name) {
      setDeptSaveError('Department name is required');
      return;
    }
    const description = deptFormDescription.trim();
    try {
      setDeptSaveError('');
      if (deptFormMode === 'edit') {
        const res = await axios.post('/departments/update', {
          id: deptFormId,
          name,
          description,
        });
        const updated = res.data;
        setTenantDepartments((prev) =>
          prev.map((d) => (d.id === updated.id ? updated : d)).sort((a, b) => a.name.localeCompare(b.name))
        );
        syncDepartmentInMappings(updated.id, updated.name, updated.description);
      } else {
        const res = await axios.post('/departments/create', { name, description });
        const created = res.data;
        setTenantDepartments((prev) => [...prev, created].sort((a, b) => a.name.localeCompare(b.name)));
        setAgentDepartmentEmails((prev) => {
          if (prev.some((m) => m.departmentId === created.id)) return prev;
          return [
            ...prev,
            {
              departmentId: created.id,
              departmentName: created.name,
              departmentDescription: created.description,
              emails: '',
            },
          ];
        });
      }
      setDeptFormName('');
      setDeptFormDescription('');
      setDeptFormId(null);
      setScreen(deptFormReturnScreen);
    } catch (error) {
      const msg = error?.response?.data?.error || `Failed to ${deptFormMode === 'edit' ? 'update' : 'create'} department`;
      setDeptSaveError(msg);
    }
  };

  const handleDeleteDepartment = async (dept) => {
    if (!window.confirm(`Delete department "${dept.name}"? This cannot be undone.`)) {
      return;
    }
    try {
      await axios.post('/departments/delete', null, { params: { id: dept.id } });
      setTenantDepartments((prev) => prev.filter((d) => d.id !== dept.id));
      setAgentDepartmentEmails((prev) => prev.filter((m) => m.departmentId !== dept.id));
    } catch (error) {
      const msg = error?.response?.data?.error || 'Failed to delete department';
      if (setAlert) {
        setAlert({ severity: 'error', message: msg });
        setTimeout(() => setAlert(null), 4000);
      }
    }
  };

  const handleSave = async () => {
    if (!agentId) return;
    setSaving(true);
    try {
      const updatedCustomization = {
        ...customization,
        departmentEmailNotificationsEnabled: enabled,
      };
      await axios.post('/agents/saveAgentCustomization', updatedCustomization, {
        params: { agentId },
      });
      await axios.post(
        '/agents/saveAgentDepartmentEmails',
        agentDepartmentEmails.map((m) => ({
          departmentId: m.departmentId,
          emails: m.emails || '',
        })),
        { params: { agentId } }
      );
      setCustomization(updatedCustomization);
      setSnapshotEnabled(enabled);
      setSnapshotMappings(JSON.parse(JSON.stringify(agentDepartmentEmails)));
      if (onSaved) {
        onSaved(updatedCustomization, agentDepartmentEmails);
      }
      if (setAlert) {
        setAlert({ severity: 'success', message: 'Advanced email settings saved successfully!' });
        setTimeout(() => setAlert(null), 2500);
      }
      onClose();
    } catch (error) {
      console.error('Error saving advanced email settings:', error);
      const d = error?.response?.data;
      const apiMsg = typeof d === 'string' ? d : d?.error || d?.message;
      if (setAlert) {
        setAlert({ severity: 'error', message: apiMsg || 'Failed to save advanced email settings' });
        setTimeout(() => setAlert(null), 3000);
      }
    } finally {
      setSaving(false);
    }
  };

  const dialogTitle = () => {
    if (screen === 'departments') return 'Departments';
    if (screen === 'deptForm') return deptFormMode === 'edit' ? 'Edit Department' : 'Add New Department';
    return 'Advanced Department Routing';
  };

  const showMainActions = screen === 'main';

  return (
    <Dialog
      open={open}
      onClose={handleRequestClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          maxWidth: 720,
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle sx={{ pr: 5, flexShrink: 0 }}>
        {dialogTitle()}
        <IconButton
          aria-label="close"
          onClick={handleRequestClose}
          sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}>
        {screen === 'main' && (
          <Box sx={{ pt: 0.5 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Route notifications by department. The AI will classify each chat and include matching department emails
              along with your global notification recipients.
            </Typography>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography>Enable department routing</Typography>
                <Switch checked={enabled} onChange={(e) => setEnabled(e.target.checked)} color="primary" />
              </Box>
            </FormControl>

            {enabled && (
              <>
                <FormControl fullWidth sx={{ mb: 1 }} disabled={deptDataLoading}>
                  <InputLabel>Department</InputLabel>
                  <Select value={deptSelectValue} onChange={handleSelectDepartment} label="Department">
                    <MenuItem value="">
                      <em>Select a department</em>
                    </MenuItem>
                    {tenantDepartments
                      .filter((d) => !agentDepartmentEmails.some((m) => m.departmentId === d.id))
                      .map((d) => (
                        <MenuItem key={d.id} value={d.id}>
                          {d.name}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>

                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                  <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={() => openAddDeptForm('main')}>
                    Add New Department
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => {
                      setDeptSaveError('');
                      setScreen('departments');
                    }}
                  >
                    Edit Departments
                  </Button>
                </Box>

                {deptDataLoading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                )}

                {!deptDataLoading && agentDepartmentEmails.length === 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Select a department above and enter notification emails for each.
                  </Typography>
                )}

                {agentDepartmentEmails.map((mapping) => {
                  const invalidEmails = getInvalidEmailsInCsv(mapping.emails);
                  return (
                    <Box
                      key={mapping.departmentId}
                      sx={{ mb: 2, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Typography variant="subtitle2">{mapping.departmentName}</Typography>
                        <IconButton
                          size="small"
                          aria-label="Remove department"
                          onClick={() => handleRemoveDepartmentMapping(mapping.departmentId)}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      <TextField
                        fullWidth
                        label="Department Notification Emails"
                        multiline
                        minRows={2}
                        maxRows={4}
                        value={mapping.emails}
                        onChange={handleDepartmentEmailsChange(mapping.departmentId)}
                        placeholder="team@example.com, lead@example.com"
                        error={invalidEmails.length > 0}
                        helperText={
                          invalidEmails.length > 0
                            ? `Invalid email${invalidEmails.length > 1 ? 's' : ''}: ${invalidEmails.join(', ')}`
                            : 'Comma-separated email addresses for this department'
                        }
                      />
                    </Box>
                  );
                })}
              </>
            )}
          </Box>
        )}

        {screen === 'departments' && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Account-wide departments shared across agents. Edit names and descriptions or remove unused departments.
            </Typography>
            {tenantDepartments.length === 0 && !deptDataLoading && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                No departments yet.
              </Typography>
            )}
            {tenantDepartments.map((dept) => (
              <Box
                key={dept.id}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  mb: 1,
                  p: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                }}
              >
                <Box sx={{ pr: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {dept.name}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexShrink: 0 }}>
                  <IconButton size="small" aria-label={`Edit ${dept.name}`} onClick={() => openEditDeptForm(dept)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" aria-label={`Delete ${dept.name}`} onClick={() => handleDeleteDepartment(dept)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            ))}
            <Button startIcon={<AddIcon />} onClick={() => openAddDeptForm('departments')} sx={{ mt: 1 }}>
              Add Department
            </Button>
          </Box>
        )}

        {screen === 'deptForm' && (
          <Box sx={{ pt: 0.5 }}>
            <TextField
              autoFocus
              margin="dense"
              label="Department Name"
              fullWidth
              required
              value={deptFormName}
              onChange={(e) => setDeptFormName(e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label="Description (optional)"
              fullWidth
              multiline
              minRows={2}
              value={deptFormDescription}
              onChange={(e) => setDeptFormDescription(e.target.value)}
              helperText="Helps the AI classify chats into the right department"
            />
            {deptSaveError && (
              <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                {deptSaveError}
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, flexShrink: 0, justifyContent: showMainActions ? 'space-between' : 'flex-end' }}>
        {showMainActions ? (
          <>
            <Button onClick={handleRequestClose} color="inherit">
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              color="primary"
              startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </>
        ) : screen === 'departments' ? (
          <Button onClick={() => setScreen('main')}>Back</Button>
        ) : (
          <>
            <Button onClick={() => setScreen(deptFormReturnScreen)}>Back</Button>
            <Button onClick={handleSaveDepartment} color="primary" variant="contained">
              {deptFormMode === 'edit' ? 'Save Changes' : 'Add Department'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AdvancedEmailSettingsModal;
